(function (global, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  global.MdvShopTheLookCore = api;
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  var LOCK_PREFIX = 'mdv-stl-cart-write:';

  function routeRoot(shopify) {
    var value = shopify && shopify.routes && shopify.routes.root;
    return typeof value === 'string' && /^\/[a-z]{0,2}(?:-[a-z]{2})?\/?$/i.test(value) ? value : '/';
  }

  function optionIds(value) {
    if (Array.isArray(value)) return value.map(String).filter(Boolean);
    return String(value || '').split(',').map(function (item) { return item.trim(); }).filter(Boolean);
  }

  function selectionMatches(requested, returned) {
    var left = optionIds(requested);
    var right = optionIds(returned);
    return left.length === right.length && left.every(function (id, index) { return id === right[index]; });
  }

  function normalizedCode(value, length) {
    var code = String(value || '').trim().toUpperCase();
    return new RegExp('^[A-Z]{' + length + '}$').test(code) ? code : '';
  }

  function productContextStatus(data, expectedCountry, expectedCurrency) {
    var country = normalizedCode(data && data.country, 2);
    var currency = normalizedCode(data && data.currency, 3);
    var expectedCountryCode = normalizedCode(expectedCountry, 2);
    var expectedCurrencyCode = normalizedCode(expectedCurrency, 3);
    if (!country || !currency || !expectedCountryCode || !expectedCurrencyCode) return 'missing';
    return country === expectedCountryCode && currency === expectedCurrencyCode ? 'matching' : 'mismatch';
  }

  function cartCurrencyStatus(cart, expectedCurrency) {
    if (expectedCurrency === undefined || expectedCurrency === null || expectedCurrency === '') return 'unchecked';
    var expected = normalizedCode(expectedCurrency, 3);
    var actual = normalizedCode(cart && cart.currency, 3);
    if (!expected || !actual) return 'missing';
    return actual === expected ? 'matching' : 'mismatch';
  }

  function countryQueryValue(search, expectedCountry) {
    var values;
    try { values = new URLSearchParams(String(search || '')).getAll('country'); } catch (error) { return ''; }
    if (values.length !== 1) return '';
    var country = normalizedCode(values[0], 2);
    if (!country) return '';
    if (expectedCountry !== undefined && country !== normalizedCode(expectedCountry, 2)) return '';
    return country;
  }

  function cartDestinationUrl(cartPath, origin, pageSearch, expectedCountry) {
    var source;
    try { source = new URL(String(cartPath || '/cart'), origin); } catch (error) { return '/cart'; }
    var destination = source.pathname || '/cart';
    var country = countryQueryValue(pageSearch, expectedCountry);
    return country ? destination + '?country=' + encodeURIComponent(country) : destination;
  }

  function sectionRequestUrl(productUrl, origin, sectionId, selectedIds, pageSearch, expectedCountry) {
    var source = new URL(productUrl, origin);
    var url = new URL(source.origin + source.pathname);
    url.searchParams.set('section_id', sectionId);
    if (selectedIds && selectedIds.length) url.searchParams.set('option_values', selectedIds.join(','));
    var country = countryQueryValue(pageSearch, expectedCountry);
    if (country) url.searchParams.set('country', country);
    return url.toString();
  }

  function quantityForVariant(cart, variantId) {
    if (!cart || !Array.isArray(cart.items)) return null;
    var total = 0;
    for (var index = 0; index < cart.items.length; index += 1) {
      var item = cart.items[index];
      if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
      var identity = Object.prototype.hasOwnProperty.call(item, 'variant_id') ? item.variant_id : item.id;
      var validIdentity = typeof identity === 'number' ? Number.isSafeInteger(identity) && identity > 0 :
        typeof identity === 'string' && /^[1-9]\d*$/.test(identity);
      if (!validIdentity || typeof item.quantity !== 'number' || !Number.isSafeInteger(item.quantity) || item.quantity < 0) return null;
      if (String(identity) === String(variantId)) total += item.quantity;
      if (!Number.isSafeInteger(total)) return null;
    }
    return total;
  }

  function reconcileUncertain(beforeCart, afterCart, variantId, quantity) {
    var before = quantityForVariant(beforeCart, variantId);
    var after = quantityForVariant(afterCart, variantId);
    if (before === null || after === null) return 'uncertain';
    return after >= before + Number(quantity || 1) ? 'confirmed' : 'uncertain';
  }

  function addResponseConfirms(body, variantId, quantity) {
    if (!body || typeof body !== 'object') return false;
    var items = Array.isArray(body.items) ? body.items : [];
    return items.some(function (item) {
      var returnedId = item && (item.variant_id || item.id);
      return String(returnedId) === String(variantId) && Number(item.quantity) >= Number(quantity || 1);
    });
  }

  function cartResponseOutcome(status, ok, body, parsed, variantId, quantity) {
    if (ok && parsed && addResponseConfirms(body, variantId, quantity)) return 'accepted';
    var explicitBusinessError = (Number(status) === 400 || Number(status) === 422) && parsed && body &&
      typeof body === 'object' &&
      (typeof body.description === 'string' || typeof body.message === 'string') &&
      (body.status === undefined || Number(body.status) === Number(status));
    if (explicitBusinessError) return 'business_error';
    return 'uncertain';
  }

  function responseBody(response) {
    return response.text().then(function (text) {
      try {
        return { body: JSON.parse(text), parsed: true };
      } catch (error) {
        return { body: null, parsed: false };
      }
    }, function () {
      return { body: null, parsed: false };
    });
  }

  function errorMessage(body, fallback) {
    if (!body || typeof body !== 'object') return fallback;
    return (typeof body.description === 'string' && body.description) ||
      (typeof body.message === 'string' && body.message) || fallback;
  }

  function runCartWriteMachine(options) {
    var beforeCart = null;
    var responseOutcome = '';
    var responseBodyValue = null;

    function isCurrent() {
      return options.currentContext() === options.context;
    }

    function reconcile(kind) {
      if (!isCurrent()) return Promise.resolve({ kind: 'uncertain', stale: true });
      return Promise.resolve(options.wait ? options.wait() : null).then(function () {
        if (!isCurrent()) return { kind: 'uncertain', stale: true };
        return options.readCart().then(function (afterCart) {
          if (!isCurrent()) return { kind: 'uncertain', stale: true };
          var currencyStatus = cartCurrencyStatus(afterCart, options.expectedCurrency);
          if (currencyStatus !== 'matching' && currencyStatus !== 'unchecked') {
            return { kind: 'uncertain_context_mismatch', reason: currencyStatus, cart: afterCart };
          }
          if (reconcileUncertain(beforeCart, afterCart, options.variantId, options.quantity) === 'confirmed') {
            return {
              kind: 'confirmed',
              adjusted: kind === 'business_error',
              cart: afterCart,
              message: errorMessage(responseBodyValue, '')
            };
          }
          var beforeQuantity = quantityForVariant(beforeCart, options.variantId);
          var afterQuantity = quantityForVariant(afterCart, options.variantId);
          if (kind === 'business_error' && beforeQuantity !== null && afterQuantity !== null && beforeQuantity === afterQuantity) {
            return { kind: 'rejected', message: errorMessage(responseBodyValue, options.rejectionMessage) };
          }
          return { kind: 'uncertain', cart: afterCart };
        }, function () {
          return options.expectedCurrency ?
            { kind: 'uncertain_context_mismatch', reason: 'read_failed' } :
            { kind: 'uncertain' };
        });
      });
    }

    return options.readCart().then(function (cart) {
      return { cart: cart, read: true };
    }, function () {
      return { cart: null, read: false };
    }).then(function (preflight) {
      if (!isCurrent()) return { kind: 'aborted_stale' };
      if (options.expectedCurrency && !preflight.read) {
        return { kind: 'aborted_before_write', reason: 'read_failed' };
      }
      var preflightCurrency = cartCurrencyStatus(preflight.cart, options.expectedCurrency);
      if (preflightCurrency !== 'matching' && preflightCurrency !== 'unchecked') {
        return { kind: 'aborted_before_write', reason: preflightCurrency };
      }
      beforeCart = preflight.cart;
      options.beforeWrite();
      return options.writeCart().then(function (response) {
        return responseBody(response).then(function (result) {
          responseBodyValue = result.body;
          responseOutcome = cartResponseOutcome(response.status, response.ok, result.body, result.parsed, options.variantId, options.quantity);
          if (responseOutcome === 'accepted') {
            if (!isCurrent()) return { kind: 'confirmed_stale' };
            return options.readCart().then(function (cart) {
              if (!isCurrent()) return { kind: 'confirmed_stale' };
              var currencyStatus = cartCurrencyStatus(cart, options.expectedCurrency);
              if (currencyStatus !== 'matching' && currencyStatus !== 'unchecked') {
                return { kind: 'confirmed_context_mismatch', reason: currencyStatus, cart: cart };
              }
              return { kind: 'confirmed', cart: cart };
            }, function () {
              if (!isCurrent()) return { kind: 'confirmed_stale' };
              return options.expectedCurrency ?
                { kind: 'confirmed_context_mismatch', reason: 'read_failed' } :
                { kind: 'confirmed', cart: null };
            });
          }
          return reconcile(responseOutcome);
        });
      }, function () {
        return reconcile('uncertain');
      });
    });
  }

  function lockKey(context, variantId) {
    return LOCK_PREFIX + String(context || 'unknown') + ':' + String(variantId);
  }

  function readLock(storage, context, variantId) {
    if (!storage) return null;
    try {
      var raw = storage.getItem(lockKey(context, variantId));
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  function writeLock(storage, context, variantId, payload) {
    if (!storage) return false;
    try {
      storage.setItem(lockKey(context, variantId), JSON.stringify(payload));
      return true;
    } catch (error) {
      return false;
    }
  }

  function clearLock(storage, context, variantId) {
    if (!storage) return;
    try { storage.removeItem(lockKey(context, variantId)); } catch (error) {}
  }

  function createWriteLockRegistry(storage) {
    var memory = new Set();
    return {
      has: function (context, variantId) {
        var key = lockKey(context, variantId);
        return memory.has(key) || Boolean(readLock(storage, context, variantId));
      },
      acquire: function (context, variantId, payload) {
        memory.add(lockKey(context, variantId));
        writeLock(storage, context, variantId, payload);
      },
      clear: function (context, variantId) {
        memory.delete(lockKey(context, variantId));
        clearLock(storage, context, variantId);
      }
    };
  }

  function shouldLoadProduct(state) {
    return Boolean(state) && !state.loaded && !state.loading && !state.pending;
  }

  function hydrateStates(states, loader) {
    if (!Array.isArray(states) || typeof loader !== 'function') return Promise.resolve([]);
    var jobs = states.filter(shouldLoadProduct).map(function (state) {
      var loading;
      try {
        loading = loader(state);
      } catch (error) {
        return Promise.resolve({ state: state, status: 'rejected', reason: error });
      }
      return Promise.resolve(loading).then(function () {
        return { state: state, status: 'fulfilled' };
      }, function (error) {
        return { state: state, status: 'rejected', reason: error };
      });
    });
    return Promise.all(jobs);
  }

  function openingCanFocus(expectedToken, liveToken, expectedContext, liveContext, isOpen, isConnected, openingFocus, liveFocus) {
    return expectedToken === liveToken &&
      expectedContext === liveContext &&
      Boolean(isOpen) &&
      Boolean(isConnected) &&
      openingFocus === liveFocus;
  }

  function invalidateProductContext(state, liveContext, liveUrl) {
    if (!state || state.pending || state.context === liveContext) return false;
    state.loadGeneration = Number(state.loadGeneration || 0) + 1;
    state.requestNumber = Number(state.requestNumber || 0) + 1;
    state.context = liveContext;
    if (liveUrl) state.url = liveUrl;
    state.loaded = false;
    state.loading = null;
    state.resolving = false;
    state.variant = null;
    state.productId = '';
    state.productAvailable = true;
    state.country = '';
    state.currency = '';
    state.priceContextBlocked = false;
    state.priceContextAfterWrite = false;
    state.preorder = false;
    state.preorderLabel = '';
    state.dispatch = '';
    state.options = [];
    state.selected = {};
    state.status = '';
    state.statusType = '';
    return true;
  }

  function productLoadIsCurrent(state, generation, loadContext, liveContext) {
    return Boolean(state) &&
      state.loadGeneration === generation &&
      state.context === loadContext &&
      loadContext === liveContext;
  }

  function purchaseContextIsCurrent(loadedContext, liveContext) {
    return Boolean(loadedContext) && loadedContext === liveContext;
  }

  // Choosing an option and submitting a cart write are separate actions.
  function purchaseAction(state, liveContext, locked) {
    if (!state || !state.loaded || state.loading || state.pending || state.resolving ||
        !state.productAvailable || locked || state.statusType === 'uncertain' ||
        !purchaseContextIsCurrent(state.context, liveContext)) return 'blocked';
    if ((state.options || []).some(function (option) {
      return !state.selected[String(option.position)];
    })) return 'select';
    return state.variant && state.variant.available ? 'add' : 'blocked';
  }

  function activatePurchase(state, liveContext, locked, choose, add) {
    var action = purchaseAction(state, liveContext, locked);
    if (action === 'select') choose();
    else if (action === 'add') add();
    return action;
  }

  var activeOptionPicker = null;

  function optionPickerDomId(baseId, valueId) {
    return String(baseId) + '-option-' + encodeURIComponent(String(valueId));
  }

  function firstMissingOptionPosition(options, selected) {
    selected = selected || {};
    var missing = (options || []).find(function (option) {
      return !autoSelectSingleOption(option) && !selected[String(option.position)];
    });
    return missing ? String(missing.position) : '';
  }

  function createOptionPickerController(config) {
    var expanded = false;
    var activeIndex = -1;
    var typeahead = '';
    var lastTypeAt = 0;
    var api;

    function items() {
      return config.items() || [];
    }

    function enabled(index) {
      var item = items()[index];
      return Boolean(item && !item.disabled);
    }

    function firstEnabled() {
      return items().findIndex(function (item) { return !item.disabled; });
    }

    function lastEnabled() {
      var values = items();
      for (var index = values.length - 1; index >= 0; index -= 1) {
        if (!values[index].disabled) return index;
      }
      return -1;
    }

    function selectedIndex() {
      var selected = String(config.value() || '');
      return items().findIndex(function (item) { return String(item.value) === selected && !item.disabled; });
    }

    function sync() {
      config.sync({
        expanded: expanded,
        activeIndex: activeIndex,
        activeItem: activeIndex >= 0 ? items()[activeIndex] : null
      });
    }

    function setActive(index) {
      if (!enabled(index)) return false;
      activeIndex = index;
      sync();
      if (config.reveal) config.reveal(index);
      return true;
    }

    function close(reason) {
      if (!expanded) return false;
      expanded = false;
      typeahead = '';
      if (activeOptionPicker === api) activeOptionPicker = null;
      if (config.onClose) config.onClose(reason || 'dismiss');
      sync();
      return true;
    }

    function open(preference) {
      if (!config.canActivate()) return false;
      if (activeOptionPicker && activeOptionPicker !== api) activeOptionPicker.close('another');
      activeOptionPicker = api;
      expanded = true;
      if (preference === 'first') activeIndex = firstEnabled();
      else if (preference === 'last') activeIndex = lastEnabled();
      else {
        activeIndex = selectedIndex();
        if (activeIndex < 0) activeIndex = firstEnabled();
      }
      if (config.onOpen) config.onOpen();
      sync();
      if (config.reveal && activeIndex >= 0) config.reveal(activeIndex);
      return true;
    }

    function move(step) {
      var values = items();
      if (!values.length) return false;
      var index = activeIndex;
      for (var count = 0; count < values.length; count += 1) {
        index = (index + step + values.length) % values.length;
        if (enabled(index)) return setActive(index);
      }
      return false;
    }

    function findByTypeahead(key, now) {
      var timestamp = Number(now || Date.now());
      if (timestamp - lastTypeAt > 700) typeahead = '';
      lastTypeAt = timestamp;
      typeahead += String(key).toLocaleLowerCase();
      var values = items();
      var start = activeIndex < 0 ? 0 : activeIndex + 1;
      var match = -1;
      for (var offset = 0; offset < values.length; offset += 1) {
        var index = (start + offset) % values.length;
        if (!values[index].disabled && String(values[index].label || '').toLocaleLowerCase().indexOf(typeahead) === 0) {
          match = index;
          break;
        }
      }
      if (match < 0 && typeahead.length > 1) {
        typeahead = String(key).toLocaleLowerCase();
        return findByTypeahead('', timestamp);
      }
      return match >= 0 ? setActive(match) : false;
    }

    function commit(index) {
      var item = items()[index];
      if (!expanded || !item || item.disabled || !config.canCommit(item)) {
        close('invalid');
        return false;
      }
      close('commit');
      config.commit(item);
      return true;
    }

    function keydown(event) {
      var key = event.key;
      if (key === 'Escape') {
        if (!expanded) return false;
        event.preventDefault();
        event.stopPropagation();
        close('escape');
        return true;
      }
      if (key === 'Tab') {
        if (expanded) close('tab');
        return false;
      }
      if (key === 'Enter' || key === ' ') {
        event.preventDefault();
        if (expanded) commit(activeIndex);
        else open('selected');
        return true;
      }
      if (key === 'ArrowDown' || key === 'ArrowUp') {
        event.preventDefault();
        if (!expanded) open(key === 'ArrowDown' ? 'first' : 'last');
        else move(key === 'ArrowDown' ? 1 : -1);
        return true;
      }
      if (key === 'Home' || key === 'End') {
        event.preventDefault();
        if (!expanded) open(key === 'Home' ? 'first' : 'last');
        else setActive(key === 'Home' ? firstEnabled() : lastEnabled());
        return true;
      }
      if (key && key.length === 1 && !event.altKey && !event.ctrlKey && !event.metaKey) {
        if (!expanded && !open('selected')) return false;
        event.preventDefault();
        findByTypeahead(key, event.timeStamp);
        return true;
      }
      return false;
    }

    api = {
      open: open,
      close: close,
      commit: commit,
      keydown: keydown,
      isOpen: function () { return expanded; },
      activeIndex: function () { return activeIndex; }
    };
    return api;
  }

  function openOptionPicker(trigger) {
    if (!trigger || trigger.disabled || !trigger.__mdvOptionPicker) return false;
    trigger.scrollIntoView({ block: 'nearest' });
    trigger.focus({ preventScroll: true });
    return trigger.__mdvOptionPicker.open('selected');
  }

  // Keep keyboard position while a render temporarily disables/replaces controls.
  // Ownership ends as soon as the shopper leaves the stable wrapper.
  function createRenderFocusKeeper(anchor, optionsRoot, context, fallback) {
    var owner = null;
    var document = anchor.ownerDocument;
    anchor.tabIndex = -1;
    anchor.addEventListener('focusout', function (event) {
      if (event.target === anchor) owner = null;
    });
    return {
      before: function () {
        var liveContext = context();
        var active = document.activeElement;
        if (liveContext === null) { owner = null; return; }
        if (optionsRoot.contains(active)) {
          var identity = active.dataset.mdvStlSelect !== undefined ?
            { option: active.dataset.optionPosition } : { action: active.dataset.mdvStlAction };
          if (identity.option === undefined && identity.action === undefined) return;
          owner = { identity: identity, context: liveContext };
          anchor.focus({ preventScroll: true });
        } else if (active !== anchor || (owner && owner.context !== liveContext)) {
          owner = null;
        }
      },
      after: function (busy) {
        if (!owner) return;
        if (document.activeElement !== anchor || context() !== owner.context) { owner = null; return; }
        var identity = owner.identity;
        var replacement = Array.prototype.find.call(optionsRoot.querySelectorAll('[data-mdv-stl-select], [data-mdv-stl-action]'), function (element) {
          return identity.option !== undefined ? element.dataset.optionPosition === identity.option :
            element.dataset.mdvStlAction === identity.action;
        });
        if (replacement) replacement.setAttribute('data-mdv-stl-focus-target', '');
        var target = replacement && !replacement.disabled ? replacement : (!busy && fallback());
        if (target && !target.disabled) {
          owner = null;
          target.focus({ preventScroll: true });
        }
      }
    };
  }

  function mobileEntryIsSoldOut(state, liveContext) {
    return Boolean(state && state.loaded && !state.loading && !state.pending &&
      state.statusType !== 'uncertain' && state.statusType !== 'error' &&
      state.context === liveContext && !state.productAvailable);
  }

  function dialogReturnTarget(candidates) {
    return candidates.find(function (element) {
      return element && element.isConnected && !element.disabled &&
        typeof element.focus === 'function' && element.getClientRects().length > 0;
    });
  }

  function preorderProperties(isPreorder, dispatch) {
    if (!isPreorder) return {};
    var properties = { _pre_order: true };
    if (/^\d{4}-\d{2}-\d{2}$/.test(String(dispatch || ''))) {
      properties['Pre-order'] = 'Ships by ' + formatDispatch(dispatch);
    }
    return properties;
  }

  function formatDispatch(dispatch) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dispatch || ''))) return String(dispatch || '');
    var parts = dispatch.split('-');
    var date = new Date(Date.UTC(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2])));
    var months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return date.getUTCDate() + ' ' + months[date.getUTCMonth()] + ' ' + date.getUTCFullYear();
  }

  function autoSelectSingleOption(option) {
    if (!option || !Array.isArray(option.values) || option.values.length !== 1) return false;
    var name = String(option.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    var value = String(option.values[0].label || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    if (name.indexOf('size') !== -1 && ['onesize', 'os', 'defaulttitle'].indexOf(value) === -1) return false;
    return true;
  }

  function visibleOneSizeOption(option) {
    if (!autoSelectSingleOption(option) || !option.values || !option.values[0]) return false;
    var value = String(option.values[0].label || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    return ['onesize', 'os', 'defaulttitle'].indexOf(value) !== -1;
  }

  function telemetryConsent(root) {
    try {
      var guard = root && root.__mdvStlConsentGuard;
      if (guard && (guard.state !== 'settled' || guard.analytics !== 'yes')) return false;
      var privacy = root && root.Shopify && root.Shopify.customerPrivacy;
      if (!privacy || typeof privacy.currentVisitorConsent !== 'function' ||
        typeof privacy.analyticsProcessingAllowed !== 'function') return false;
      var consent = privacy.currentVisitorConsent();
      return Boolean(consent && consent.analytics === 'yes' && privacy.analyticsProcessingAllowed() === true);
    } catch (error) {
      return false;
    }
  }

  function safeDataLayerPush(layer, payload) {
    if (!layer || typeof layer.push !== 'function') return false;
    try {
      layer.push(payload);
      return true;
    } catch (error) {
      return false;
    }
  }

  function safeGoogleTagPush(root, measurementId, eventName, payload) {
    var allowedEvents = {
      shop_the_look_eligible: true,
      shop_the_look_exposed: true,
      shop_the_look_opened: true,
      shop_the_look_option_selected: true,
      shop_the_look_add_confirmed: true,
      shop_the_look_add_rejected: true,
      shop_the_look_add_unconfirmed: true
    };
    var allowedParams = {
      channel: true,
      entry_point: true,
      parent_product_id: true,
      product_id: true,
      variant_id: true,
      market: true,
      currency: true,
      value: true
    };
    var id = String(measurementId || '');
    if (!root || !/^G-[A-Z0-9]+$/.test(id) ||
      !Object.prototype.hasOwnProperty.call(allowedEvents, eventName)) return false;
    if (!payload || typeof payload !== 'object' || payload.event !== eventName) return false;
    var keys = Object.keys(payload);
    if (keys.some(function (key) {
      return key !== 'event' && !Object.prototype.hasOwnProperty.call(allowedParams, key);
    })) return false;
    var layer = null;
    try { layer = root.dataLayer; } catch (error) { return false; }
    if (!Array.isArray(layer)) return false;

    var params = {};
    for (var index = 0; index < keys.length; index += 1) {
      var key = keys[index];
      if (key === 'event') continue;
      var value = payload[key];
      if (key === 'value') {
        if (!Number.isFinite(value)) return false;
      } else if (typeof value !== 'string') {
        return false;
      }
      params[key] = value;
    }
    params.send_to = id;

    try {
      function googleTag() { layer.push(arguments); }
      googleTag('event', eventName, params);
      return true;
    } catch (error) {
      return false;
    }
  }

  function pushAnalytics(root, payload, candidate) {
    var dataset = candidate && candidate.dialog && candidate.dialog.dataset;
    var transport = dataset ? dataset.mdvStlAnalyticsTransport : undefined;
    if (transport === undefined) {
      var legacyLayer = null;
      try { legacyLayer = root && root.dataLayer; } catch (error) {}
      return safeDataLayerPush(legacyLayer, payload);
    }
    if (transport === 'data_layer') {
      var layer = null;
      try { layer = root && root.dataLayer; } catch (error) {}
      return safeDataLayerPush(layer, payload);
    }
    if (transport !== 'google_tag') return false;
    return safeGoogleTagPush(root, dataset.mdvStlGoogleMeasurementId, payload && payload.event, payload);
  }

  function createConsentFeatureLoader(options) {
    var inFlight = null;

    function ready() {
      try { return options.isReady() === true; } catch (error) { return false; }
    }

    function attempt() {
      if (ready()) return Promise.resolve(true);
      if (inFlight) return inFlight;
      var loading;
      try { loading = options.load(); } catch (error) { return Promise.resolve(false); }
      if (!loading || typeof loading.then !== 'function') return Promise.resolve(false);
      inFlight = Promise.resolve(loading).then(function () {
        inFlight = null;
        return ready();
      }, function () {
        inFlight = null;
        return false;
      });
      return inFlight;
    }

    return { attempt: attempt, isReady: ready };
  }

  function visibleRatio(element, viewportWidth, viewportHeight) {
    if (!element || !element.isConnected || typeof element.getBoundingClientRect !== 'function') return 0;
    try {
      var rect = element.getBoundingClientRect();
      var width = Math.max(0, Number(rect.width) || Number(rect.right) - Number(rect.left));
      var height = Math.max(0, Number(rect.height) || Number(rect.bottom) - Number(rect.top));
      if (!width || !height) return 0;
      var visibleWidth = Math.max(0, Math.min(Number(rect.right), Number(viewportWidth)) - Math.max(Number(rect.left), 0));
      var visibleHeight = Math.max(0, Math.min(Number(rect.bottom), Number(viewportHeight)) - Math.max(Number(rect.top), 0));
      return Math.min(1, (visibleWidth * visibleHeight) / (width * height));
    } catch (error) {
      return 0;
    }
  }

  function createAnalyticsController(options) {
    var eligible = [];
    var exposures = [];
    var successful = new Set();

    function safely(callback, fallback) {
      try { return callback(); } catch (error) { return fallback; }
    }

    function currentKey(candidate) {
      return String(safely(function () { return candidate.currentKey(); }, '') || '');
    }

    function valid(candidate) {
      return Boolean(candidate && safely(function () { return candidate.valid(); }, false));
    }

    function allowed() {
      return safely(options.allowed, false) === true;
    }

    function naturalAttempt() {
      if (typeof options.attemptFeature === 'function') safely(options.attemptFeature, null);
    }

    function push(candidate, eventName, detail) {
      var payload = safely(function () { return options.payload(candidate, eventName, detail || {}); }, null);
      if (!payload || typeof payload !== 'object') return false;
      return safely(function () { return options.push(payload, candidate); }, false) === true;
    }

    function ensureEligible(candidate) {
      if (!valid(candidate)) return false;
      var key = currentKey(candidate);
      if (!key) return false;
      var marker = 'eligible|' + key;
      if (successful.has(marker)) return true;
      if (!allowed() || !push(candidate, 'shop_the_look_eligible', {})) return false;
      successful.add(marker);
      return true;
    }

    function registerEligible(candidate) {
      if (candidate && !eligible.some(function (item) { return item.id === candidate.id; })) eligible.push(candidate);
      naturalAttempt();
      return ensureEligible(candidate);
    }

    function emit(candidate, eventName, detail, settings) {
      naturalAttempt();
      if (!ensureEligible(candidate)) return false;
      if (settings && settings.startAllowed === false) return false;
      if (!allowed()) return false;
      var onceMarker = settings && settings.onceKey ? 'once|' + currentKey(candidate) + '|' + settings.onceKey : '';
      if (onceMarker && successful.has(onceMarker)) return false;
      if (!push(candidate, eventName, detail)) return false;
      if (onceMarker) successful.add(onceMarker);
      return true;
    }

    function stopExposure(tracker) {
      if (!tracker || tracker.stopped) return;
      tracker.stopped = true;
      if (typeof tracker.disconnect === 'function') safely(tracker.disconnect, null);
    }

    function attemptExposure(tracker) {
      if (!tracker || tracker.stopped) return false;
      if (!safely(tracker.current, false) || !valid(tracker.candidate)) {
        stopExposure(tracker);
        return false;
      }
      if (Number(safely(tracker.currentRatio, 0)) < 0.35) return false;
      var exposureKey = String(safely(tracker.currentKey, '') || '');
      if (!exposureKey) return false;
      return emit(tracker.candidate, 'shop_the_look_exposed', tracker.detail || {}, { onceKey: 'exposure|' + exposureKey });
    }

    function registerExposure(config) {
      var duplicate = exposures.find(function (tracker) { return tracker.id === config.id; });
      if (duplicate) return duplicate;
      exposures.forEach(function (tracker) {
        if (!tracker.stopped && (!valid(tracker.candidate) || !safely(tracker.current, false))) stopExposure(tracker);
      });
      var tracker = {
        id: config.id,
        candidate: config.candidate,
        currentKey: config.currentKey,
        currentRatio: config.currentRatio,
        current: config.current || function () { return true; },
        detail: config.detail || {},
        disconnect: null,
        stopped: false
      };
      exposures.push(tracker);
      try {
        tracker.disconnect = config.observe(function () { attemptExposure(tracker); });
      } catch (error) {
        tracker.stopped = true;
      }
      return tracker;
    }

    function unregister(candidate) {
      if (!candidate) return;
      eligible = eligible.filter(function (item) { return item !== candidate; });
      exposures = exposures.filter(function (tracker) {
        if (tracker.candidate !== candidate) return true;
        stopExposure(tracker);
        return false;
      });
    }

    function refresh() {
      eligible.forEach(ensureEligible);
      exposures.forEach(function (tracker) {
        if (tracker.stopped) return;
        if (!valid(tracker.candidate) || !safely(tracker.current, false)) {
          stopExposure(tracker);
          return;
        }
        attemptExposure(tracker);
      });
    }

    return {
      allowed: allowed,
      emit: emit,
      registerEligible: registerEligible,
      registerExposure: registerExposure,
      unregister: unregister,
      refresh: refresh
    };
  }

  function channelForWidth(width) {
    return Number(width) < 768 ? 'mobile_web' : 'desktop_web';
  }

  function originalStillIndexes(slides) {
    if (!Array.isArray(slides)) return [];
    return slides.reduce(function (indexes, slide, index) {
      if (slide && !slide.cloned && !slide.video && slide.hasImage) indexes.push(index);
      return indexes;
    }, []);
  }

  function targetScrollTop(listRect, targetRect, scrollTop) {
    var listTop = Number(listRect && listRect.top);
    var targetTop = Number(targetRect && targetRect.top);
    var current = Number(scrollTop);
    if (!Number.isFinite(listTop) || !Number.isFinite(targetTop) || !Number.isFinite(current)) return Math.max(0, current || 0);
    return Math.max(0, current + targetTop - listTop);
  }

  return {
    LOCK_PREFIX: LOCK_PREFIX,
    routeRoot: routeRoot,
    optionIds: optionIds,
    selectionMatches: selectionMatches,
    productContextStatus: productContextStatus,
    cartCurrencyStatus: cartCurrencyStatus,
    countryQueryValue: countryQueryValue,
    cartDestinationUrl: cartDestinationUrl,
    sectionRequestUrl: sectionRequestUrl,
    quantityForVariant: quantityForVariant,
    reconcileUncertain: reconcileUncertain,
    addResponseConfirms: addResponseConfirms,
    cartResponseOutcome: cartResponseOutcome,
    runCartWriteMachine: runCartWriteMachine,
    lockKey: lockKey,
    readLock: readLock,
    writeLock: writeLock,
    clearLock: clearLock,
    createWriteLockRegistry: createWriteLockRegistry,
    shouldLoadProduct: shouldLoadProduct,
    hydrateStates: hydrateStates,
    openingCanFocus: openingCanFocus,
    invalidateProductContext: invalidateProductContext,
    productLoadIsCurrent: productLoadIsCurrent,
    purchaseContextIsCurrent: purchaseContextIsCurrent,
    purchaseAction: purchaseAction,
    activatePurchase: activatePurchase,
    optionPickerDomId: optionPickerDomId,
    firstMissingOptionPosition: firstMissingOptionPosition,
    createOptionPickerController: createOptionPickerController,
    openOptionPicker: openOptionPicker,
    createRenderFocusKeeper: createRenderFocusKeeper,
    mobileEntryIsSoldOut: mobileEntryIsSoldOut,
    dialogReturnTarget: dialogReturnTarget,
    preorderProperties: preorderProperties,
    formatDispatch: formatDispatch,
    autoSelectSingleOption: autoSelectSingleOption,
    visibleOneSizeOption: visibleOneSizeOption,
    telemetryConsent: telemetryConsent,
    safeDataLayerPush: safeDataLayerPush,
    safeGoogleTagPush: safeGoogleTagPush,
    pushAnalytics: pushAnalytics,
    createConsentFeatureLoader: createConsentFeatureLoader,
    visibleRatio: visibleRatio,
    createAnalyticsController: createAnalyticsController,
    channelForWidth: channelForWidth,
    originalStillIndexes: originalStillIndexes,
    targetScrollTop: targetScrollTop
  };
});

(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  if (window.MdvShopTheLook && window.MdvShopTheLook.contract === 1) {
    window.MdvShopTheLook.initAll(document);
    return;
  }

  var core = window.MdvShopTheLookCore;
  var productFetchTimeout = 10000;
  var cartWriteTimeout = 12000;
  var sessionStorage = null;
  try { sessionStorage = window.sessionStorage; } catch (error) {}
  var writeLocks = core.createWriteLockRegistry(sessionStorage);

  function fetchWithTimeout(url, options, timeout) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var requestOptions = Object.assign({ credentials: 'same-origin' }, options || {});
    if (controller) requestOptions.signal = controller.signal;
    return new Promise(function (resolve, reject) {
      var settled = false;
      var timer = null;

      function finish(callback, value) {
        if (settled) return;
        settled = true;
        if (timer !== null) window.clearTimeout(timer);
        callback(value);
      }

      timer = window.setTimeout(function () {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        if (controller) {
          try { controller.abort(); } catch (error) {}
        }
        var timeoutError = new Error('request-timeout');
        timeoutError.name = 'TimeoutError';
        reject(timeoutError);
      }, timeout);

      try {
        Promise.resolve(fetch(url, requestOptions))
          .then(function (response) {
            return response.text().then(function (text) {
              return {
                ok: response.ok,
                status: response.status,
                text: function () { return Promise.resolve(text); },
                json: function () {
                  return Promise.resolve().then(function () { return JSON.parse(text); });
                }
              };
            });
          })
          .then(function (response) { finish(resolve, response); }, function (error) { finish(reject, error); });
      } catch (error) {
        finish(reject, error);
      }
    });
  }

  function contextCountry() {
    var country = String(window.Shopify && window.Shopify.country || '').trim().toUpperCase();
    if (/^[A-Z]{2}$/.test(country)) return country;
    var dialogs = document.querySelectorAll('[data-mdv-stl-dialog]');
    for (var index = 0; index < dialogs.length; index += 1) {
      var dialog = dialogs[index];
      if (!dialog || !dialog.isConnected || dialog.__mdvDestroyed) continue;
      country = String(dialog.dataset && dialog.dataset.market || '').trim().toUpperCase();
      if (/^[A-Z]{2}$/.test(country)) return country;
    }
    return '';
  }

  function currentContext() {
    var currency = window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : '';
    var accountState = document.body.classList.contains('customer-logged-in') ? 'signed_in' : 'guest';
    var country = contextCountry();
    return [window.location.host, core.routeRoot(window.Shopify), currency || '', country, document.documentElement.lang || '', accountState].join('|');
  }

  var consentFeature = core.createConsentFeatureLoader({
    isReady: function () {
      var privacy = window.Shopify && window.Shopify.customerPrivacy;
      return Boolean(privacy && typeof privacy.analyticsProcessingAllowed === 'function' &&
        typeof privacy.currentVisitorConsent === 'function');
    },
    load: function () {
      var shopify = window.Shopify;
      if (!shopify || typeof shopify.loadFeatures !== 'function') return null;
      return new Promise(function (resolve) {
        shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
          resolve(!error);
        });
      });
    }
  });
  var telemetry = null;

  function telemetryPayload(candidate, eventName, detail) {
    var dialog = candidate.dialog;
    var payload = {
      event: eventName,
      channel: core.channelForWidth(window.innerWidth),
      entry_point: detail.entryPoint || '',
      parent_product_id: dialog.dataset.parentProductId || '',
      product_id: detail.productId || '',
      variant_id: detail.variantId || '',
      market: dialog.dataset.market || '',
      currency: dialog.dataset.currency || ''
    };
    if (Number.isFinite(detail.value)) payload.value = detail.value;
    return payload;
  }

  function attemptConsentFeature() {
    var wasReady = consentFeature.isReady();
    return consentFeature.attempt().then(function (ready) {
      if (ready && !wasReady && telemetry) telemetry.refresh();
      return ready;
    }, function () { return false; });
  }

  telemetry = core.createAnalyticsController({
    allowed: function () { return core.telemetryConsent(window); },
    push: function (payload, candidate) { return core.pushAnalytics(window, payload, candidate); },
    payload: telemetryPayload,
    attemptFeature: function () { attemptConsentFeature(); }
  });

  function analytics(dialog, eventName, detail, settings) {
    var candidate = dialog && dialog.__mdvTelemetryCandidate;
    if (!candidate) return false;
    return telemetry.emit(candidate, eventName, detail || {}, settings || {});
  }

  function telemetryIdentityIsCurrent(dialog) {
    if (!dialog || dialog.__mdvDestroyed || !dialog.isConnected) return false;
    var parentId = String(dialog.dataset.parentProductId || '');
    var parentHandle = String(dialog.dataset.parentProductHandle || '');
    if (!parentId || !parentHandle || productHandle(window.location.href) !== parentHandle) return false;
    var productRoot = dialog.closest('.product-page--root');
    if (!productRoot) return false;
    var displayedId = String(productRoot.dataset.productId || productRoot.getAttribute('data-product-id') || '');
    return !displayedId || displayedId === parentId;
  }

  function registerTelemetry(dialog) {
    var candidate = {
      id: dialog,
      dialog: dialog,
      currentKey: function () {
        return currentContext() + '|' + (dialog.dataset.parentProductId || '');
      },
      valid: function () {
        return Boolean(dialog.isConnected && dialog.dataset.mdvStlReady === 'true' &&
          dialog.dataset.parentProductId && dialog.__mdvStates && dialog.__mdvStates.size > 0 &&
          dialog.dataset.mdvStlDisabled !== 'true' && dialog.getAttribute('aria-disabled') !== 'true' &&
          telemetryIdentityIsCurrent(dialog));
      }
    };
    dialog.__mdvTelemetryCandidate = candidate;
    telemetry.registerEligible(candidate);
    attemptConsentFeature();
  }

  function productHandle(url) {
    try {
      var parts = new URL(url, window.location.origin).pathname.split('/products/');
      return parts.length > 1 ? parts[1].split('/')[0] : '';
    } catch (error) {
      return '';
    }
  }

  function parseProductHtml(html) {
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var root = parsed.querySelector('[data-mdv-stl-product]');
    if (!root) throw new Error('missing-product-data');
    var options = Array.prototype.map.call(root.querySelectorAll('[data-mdv-stl-option]'), function (option) {
      return {
        position: Number(option.dataset.optionPosition),
        name: option.dataset.optionName || '',
        values: Array.prototype.map.call(option.querySelectorAll('[data-mdv-stl-option-value]'), function (value) {
          return {
            id: value.dataset.optionValueId || '',
            label: value.dataset.optionValue || '',
            available: value.dataset.optionValueAvailable === 'true',
            selected: value.dataset.optionValueSelected === 'true',
            variantId: value.dataset.optionValueVariantId || ''
          };
        })
      };
    }).sort(function (a, b) { return a.position - b.position; });
    return {
      country: root.dataset.country || '',
      currency: root.dataset.currency || '',
      productId: root.dataset.productId || '',
      handle: root.dataset.productHandle || '',
      productAvailable: root.dataset.productAvailable === 'true',
      selectedOptionIds: core.optionIds(root.dataset.selectedOptionIds),
      variantId: root.dataset.selectedVariantId || '',
      available: root.dataset.selectedVariantAvailable === 'true',
      tracked: root.dataset.selectedVariantTracked === 'true' ? true :
        (root.dataset.selectedVariantTracked === 'false' ? false : null),
      policy: root.dataset.selectedVariantPolicy || '',
      requiresShipping: root.dataset.selectedVariantRequiresShipping === 'true' ? true :
        (root.dataset.selectedVariantRequiresShipping === 'false' ? false : null),
      price: Number(root.dataset.selectedVariantPrice || 0),
      formattedPrice: root.dataset.selectedVariantPriceFormatted || '',
      compareAtPrice: Number(root.dataset.selectedVariantCompareAtPrice || 0),
      formattedCompareAtPrice: root.dataset.selectedVariantCompareAtPriceFormatted || '',
      preorder: root.dataset.preorder === 'true',
      preorderLabel: root.dataset.preorderLabel || '',
      dispatch: root.dataset.preorderDispatch || '',
      options: options
    };
  }

  function sectionUrl(state, dialog, selectedIds) {
    return core.sectionRequestUrl(
      state.url,
      window.location.origin,
      dialog.dataset.sectionId,
      selectedIds,
      window.location.search,
      dialog.__mdvPageMarket || dialog.dataset.market
    );
  }

  function button(label, className, action) {
    var element = document.createElement('button');
    element.type = 'button';
    element.className = className;
    element.dataset.mdvStlAction = action;
    element.textContent = label;
    return element;
  }

  function recordInlineEngagement(state, dialog, surface, event) {
    if (surface !== 'inline' || core.channelForWidth(window.innerWidth) !== 'desktop_web') return;
    var target = event.target.closest('[data-mdv-stl-select], [data-mdv-stl-action]');
    if (!target || target.disabled || target.dataset.mdvStlAction === 'view-bag') return;
    if (event.type === 'keydown') {
      var picker = target.dataset.mdvStlSelect !== undefined;
      var keys = picker ?
        ['Enter', ' ', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown'] : ['Enter', ' '];
      var typeahead = picker && typeof event.key === 'string' && event.key.length === 1 &&
        !event.altKey && !event.ctrlKey && !event.metaKey;
      if (keys.indexOf(event.key) === -1 && !typeahead) return;
    }
    analytics(dialog, 'shop_the_look_opened', {
      entryPoint: 'existing_section',
      productId: state.productId
    }, { onceKey: 'opened|existing_section' });
  }

  function lockedReviewIsCurrent(state) {
    var liveContext = currentContext();
    return Boolean(state && state.loaded && state.variant &&
      !state.loading && !state.resolving && !state.pending && !state.priceContextBlocked &&
      core.purchaseContextIsCurrent(state.context, liveContext) &&
      writeLocks.has(state.context, state.variant.id));
  }

  function closeOptionPickers(root, reason) {
    Array.prototype.forEach.call(root.querySelectorAll('[data-mdv-stl-select]'), function (trigger) {
      try {
        if (trigger.__mdvOptionPicker) trigger.__mdvOptionPicker.close(reason || 'cleanup');
      } catch (error) {}
    });
  }

  function optionPickerStateIsCurrent(state, optionPosition) {
    if (!state.loaded || state.loading || state.pending || state.resolving || state.priceContextBlocked ||
        !core.purchaseContextIsCurrent(state.context, currentContext())) return false;
    var optionIndex = (state.options || []).findIndex(function (option) {
      return String(option.position) === String(optionPosition);
    });
    if (optionIndex < 0) return false;
    return state.options.slice(0, optionIndex).every(function (option) {
      return core.autoSelectSingleOption(option) || Boolean(state.selected[String(option.position)]);
    });
  }

  function optionPickerCanActivate(trigger, state, optionPosition) {
    return Boolean(trigger.isConnected && !trigger.disabled && optionPickerStateIsCurrent(state, optionPosition));
  }

  function prepareOptionPickerLayout(trigger, listbox) {
    var field = trigger.closest('.mdv-stl-control__field');
    var scrollport = trigger.closest('.mdv-stl-dialog__list');
    var swiper = !scrollport ? trigger.closest('.new-shop-the-look .swiper') : null;
    var layout = { field: field, listbox: listbox, scrollport: scrollport, swiper: swiper, spacer: null };
    listbox.hidden = false;
    field.dataset.mdvStlPickerOpen = 'true';
    field.scrollIntoView({ block: 'nearest' });
    var desiredHeight = Math.min(216, Math.max(36, listbox.scrollHeight || 0));
    var triggerRect = trigger.getBoundingClientRect();
    var boundary = scrollport ? scrollport.getBoundingClientRect().bottom : window.innerHeight;
    var usableHeight = Math.max(0, boundary - triggerRect.bottom);
    if (scrollport && usableHeight < desiredHeight) {
      var spacer = document.createElement('div');
      spacer.className = 'mdv-stl-picker-spacer';
      spacer.setAttribute('aria-hidden', 'true');
      spacer.style.height = Math.ceil(desiredHeight - usableHeight + 8) + 'px';
      scrollport.appendChild(spacer);
      layout.spacer = spacer;
      scrollport.scrollTop += Math.ceil(desiredHeight - usableHeight + 8);
      triggerRect = trigger.getBoundingClientRect();
      usableHeight = Math.max(0, scrollport.getBoundingClientRect().bottom - triggerRect.bottom);
    }
    if (swiper) {
      var swiperRect = swiper.getBoundingClientRect();
      var trailingSpace = Math.max(0, desiredHeight - Math.max(0, swiperRect.bottom - triggerRect.bottom));
      layout.paddingBottom = swiper.style.paddingBottom;
      layout.boxSizing = swiper.style.boxSizing;
      if (trailingSpace) {
        swiper.style.boxSizing = 'content-box';
        swiper.style.paddingBottom = Math.ceil(trailingSpace) + 'px';
        swiper.dataset.mdvStlPickerSpace = 'true';
      }
      usableHeight = Math.max(0, window.innerHeight - trigger.getBoundingClientRect().bottom);
    }
    listbox.style.maxHeight = Math.max(36, Math.min(desiredHeight, usableHeight || desiredHeight)) + 'px';
    return layout;
  }

  function releaseOptionPickerLayout(layout) {
    if (!layout) return;
    if (layout.spacer && layout.spacer.isConnected) layout.spacer.remove();
    if (layout.swiper) {
      layout.swiper.style.paddingBottom = layout.paddingBottom || '';
      layout.swiper.style.boxSizing = layout.boxSizing || '';
      delete layout.swiper.dataset.mdvStlPickerSpace;
    }
    if (layout.field) delete layout.field.dataset.mdvStlPickerOpen;
    if (layout.listbox) layout.listbox.style.maxHeight = '';
  }

  function commitOptionSelection(state, dialog, surface, optionPosition, valueId) {
    if (!optionPickerStateIsCurrent(state, optionPosition)) return false;
    var option = (state.options || []).find(function (candidate) {
      return String(candidate.position) === String(optionPosition);
    });
    var value = option && option.values.find(function (candidate) {
      return String(candidate.id) === String(valueId);
    });
    if (!value || !value.available) return false;
    state.entryPoint = surface === 'inline' ? 'existing_section' : state.entryPoint;
    state.selected[String(optionPosition)] = value.id;
    state.variant = null;
    state.status = '';
    state.statusType = '';
    analytics(dialog, 'shop_the_look_option_selected', {
      entryPoint: state.entryPoint || (surface === 'panel' ? 'gallery' : 'existing_section'),
      productId: state.productId
    });
    renderState(state, dialog);
    resolveIfComplete(state, dialog);
    return true;
  }

  function createOptionPicker(state, dialog, control, field, label, option, previousOptionSelected) {
    var surface = control.dataset.mdvStlSurface;
    var baseId = 'mdv-stl-' + state.handle + '-' + surface + '-' + option.position;
    var trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.id = baseId;
    trigger.className = 'mdv-stl-control__select';
    trigger.dataset.mdvStlSelect = '';
    trigger.dataset.optionPosition = String(option.position);
    trigger.dataset.placeholder = dialog.dataset.labelChoose.replace('__OPTION__', String(option.name).toLowerCase() === 'size' ? 'size' : option.name);
    trigger.setAttribute('role', 'combobox');
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    label.id = baseId + '-label';
    label.htmlFor = baseId;
    trigger.setAttribute('aria-labelledby', label.id + ' ' + baseId);
    var selectedId = state.selected[String(option.position)] || '';
    var selectedValue = option.values.find(function (value) { return String(value.id) === String(selectedId); });
    trigger.value = selectedId;
    trigger.textContent = selectedValue ? selectedValue.label : trigger.dataset.placeholder;
    trigger.disabled = state.pending || state.resolving || !previousOptionSelected;

    var listbox = document.createElement('div');
    listbox.id = baseId + '-listbox';
    listbox.className = 'mdv-stl-control__listbox';
    listbox.setAttribute('role', 'listbox');
    listbox.setAttribute('aria-labelledby', label.id);
    listbox.hidden = true;
    trigger.setAttribute('aria-controls', listbox.id);
    var items = option.values.map(function (value) {
      var element = document.createElement('div');
      element.id = core.optionPickerDomId(baseId, value.id);
      element.className = 'mdv-stl-control__option';
      element.dataset.mdvStlOption = '';
      element.dataset.optionIndex = String(listbox.children.length);
      element.setAttribute('role', 'option');
      element.setAttribute('aria-disabled', value.available ? 'false' : 'true');
      element.setAttribute('aria-selected', String(String(value.id) === String(selectedId)));
      element.textContent = value.label;
      listbox.appendChild(element);
      return { value: value.id, label: value.label, disabled: !value.available, id: element.id, element: element };
    });
    var layout = null;
    var outside = null;
    var controller = core.createOptionPickerController({
      items: function () { return items; },
      value: function () { return state.selected[String(option.position)] || ''; },
      canActivate: function () { return optionPickerCanActivate(trigger, state, option.position); },
      canCommit: function (item) {
        if (!optionPickerCanActivate(trigger, state, option.position)) return false;
        var liveOption = state.options.find(function (candidate) { return String(candidate.position) === String(option.position); });
        var liveValue = liveOption && liveOption.values.find(function (candidate) { return String(candidate.id) === String(item.value); });
        return Boolean(liveValue && liveValue.available);
      },
      sync: function (pickerState) {
        trigger.setAttribute('aria-expanded', String(pickerState.expanded));
        listbox.hidden = !pickerState.expanded;
        if (pickerState.expanded && pickerState.activeItem) trigger.setAttribute('aria-activedescendant', pickerState.activeItem.id);
        else trigger.removeAttribute('aria-activedescendant');
        items.forEach(function (item, index) {
          item.element.dataset.active = String(pickerState.expanded && index === pickerState.activeIndex);
          item.element.setAttribute('aria-selected', String(String(item.value) === String(state.selected[String(option.position)] || '')));
        });
      },
      reveal: function (index) {
        var element = items[index] && items[index].element;
        if (element) element.scrollIntoView({ block: 'nearest' });
      },
      onOpen: function () {
        layout = prepareOptionPickerLayout(trigger, listbox);
        outside = function (event) {
          if (!field.contains(event.target)) controller.close('outside');
        };
        document.addEventListener('pointerdown', outside, true);
      },
      onClose: function () {
        if (outside) document.removeEventListener('pointerdown', outside, true);
        outside = null;
        releaseOptionPickerLayout(layout);
        layout = null;
      },
      commit: function (item) {
        commitOptionSelection(state, dialog, surface, option.position, item.value);
      }
    });
    trigger.__mdvOptionPicker = controller;
    trigger.addEventListener('click', function () {
      if (controller.isOpen()) controller.close('toggle');
      else core.openOptionPicker(trigger);
    });
    trigger.addEventListener('keydown', function (event) { controller.keydown(event); });
    listbox.addEventListener('pointerdown', function (event) {
      if (event.target.closest('[data-mdv-stl-option]')) event.preventDefault();
    });
    listbox.addEventListener('click', function (event) {
      var item = event.target.closest('[data-mdv-stl-option]');
      if (!item) return;
      event.preventDefault();
      trigger.focus({ preventScroll: true });
      if (item.getAttribute('aria-disabled') === 'true') return;
      controller.commit(Number(item.dataset.optionIndex));
    });
    field.appendChild(label);
    field.appendChild(trigger);
    field.appendChild(listbox);
    return trigger;
  }

  function createControl(state, dialog, surface) {
    var control = document.createElement('div');
    control.className = 'mdv-stl-control';
    control.dataset.mdvStlControl = state.handle;
    control.dataset.mdvStlSurface = surface;
    var options = document.createElement('div');
    options.className = 'mdv-stl-control__options';
    options.dataset.mdvStlOptions = '';
    var status = document.createElement('p');
    status.className = 'mdv-stl-control__status';
    status.id = 'mdv-stl-' + state.handle + '-' + surface + '-status';
    status.dataset.mdvStlStatus = '';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    control.appendChild(options);
    control.appendChild(status);
    control.__mdvFocusKeeper = core.createRenderFocusKeeper(control, options, function () {
      if (!control.isConnected || !control.getClientRects().length ||
          (surface === 'panel' ? !dialog.open : dialog.open)) return null;
      return currentContext() + '|' + (dialog.__mdvOpening || 0);
    }, function () {
      var viewBag = surface === 'panel' ? dialog.querySelector('[data-mdv-stl-view-bag]') :
        options.querySelector('[data-mdv-stl-action="view-bag"]');
      if ((state.statusType === 'uncertain' || lockedReviewIsCurrent(state)) && viewBag && !viewBag.hidden) return viewBag;
      return options.querySelector('[data-mdv-stl-select]:not([disabled]), button:not([disabled])') ||
        control.closest('.swiper-slide, [data-mdv-stl-panel-item]').querySelector('a[href]');
    });
    control.addEventListener('pointerdown', function (event) { recordInlineEngagement(state, dialog, surface, event); });
    control.addEventListener('keydown', function (event) { recordInlineEngagement(state, dialog, surface, event); });
    control.addEventListener('click', function (event) {
      var action = event.target.closest('[data-mdv-stl-action]');
      if (!action) return;
      state.entryPoint = surface === 'inline' ? 'existing_section' : state.entryPoint;
      if (action.dataset.mdvStlAction === 'load') {
        recordInlineEngagement(state, dialog, surface, event);
        loadState(state, dialog);
      }
      if (action.dataset.mdvStlAction === 'add') {
        recordInlineEngagement(state, dialog, surface, event);
        var variantContext = state.context || currentContext();
        var locked = state.variant && writeLocks.has(variantContext, state.variant.id);
        core.activatePurchase(state, currentContext(), locked, function () {
          var missingPosition = core.firstMissingOptionPosition(state.options, state.selected);
          var missing = Array.prototype.find.call(control.querySelectorAll('[data-mdv-stl-select]:not([disabled])'), function (trigger) {
            return trigger.dataset.optionPosition === missingPosition;
          });
          if (!missing) return;
          var prompt = missing.dataset.placeholder;
          setStatus(state, prompt, '');
          state.controls.forEach(function (entry) {
            var feedback = entry.querySelector('[data-mdv-stl-status]');
            feedback.textContent = prompt;
            feedback.dataset.state = '';
          });
          missing.setAttribute('aria-describedby', status.id);
          core.openOptionPicker(missing);
        }, function () { addItem(state, dialog); });
      }
      if (action.dataset.mdvStlAction === 'view-bag') {
        openBagDrawer(dialog, control);
      }
    });
    state.controls.push(control);
    return control;
  }

  function setStatus(state, message, type) {
    state.status = message || '';
    state.statusType = type || '';
  }

  function allSelected(state) {
    return state.options && state.options.every(function (option) {
      return Boolean(state.selected[String(option.position)]);
    });
  }

  function selectedIds(state) {
    return (state.options || []).map(function (option) {
      return state.selected[String(option.position)] || '';
    }).filter(Boolean);
  }

  function updateDisplayedPrice(control, state) {
    var card = control.closest('.swiper-slide, [data-mdv-stl-panel-item]');
    var priceWrap = card && card.querySelector('.mdv-tile-price-wrap');
    if (!priceWrap) return;
    if (priceWrap.__mdvOriginalPrice === undefined) priceWrap.__mdvOriginalPrice = priceWrap.innerHTML;
    if (!state.variant) {
      priceWrap.innerHTML = priceWrap.__mdvOriginalPrice;
      return;
    }
    var price = priceWrap.querySelector('.mdv-tile-price');
    if (!price) return;
    var onSale = state.variant.compareAtPrice > state.variant.price;
    price.textContent = state.variant.formattedPrice;
    price.classList.toggle('onsale', onSale);
    priceWrap.classList.toggle('onsale', onSale);
    var compare = priceWrap.querySelector('.mdv-tile-compare');
    if (onSale) {
      if (!compare) {
        compare = document.createElement('s');
        compare.className = 'mdv-tile-compare compare-price';
        priceWrap.appendChild(compare);
      }
      compare.textContent = state.variant.formattedCompareAtPrice;
    } else if (compare) {
      compare.remove();
    }
  }

  function renderState(state, dialog) {
    if (dialog.__mdvDestroyed ||
        (state.instanceGeneration !== undefined && state.instanceGeneration !== dialog.__mdvInstanceGeneration)) return;
    var lockedReview = lockedReviewIsCurrent(state);
    if (lockedReview) dialog.querySelector('[data-mdv-stl-view-bag]').hidden = false;
    if (state.mobileFocus) {
      var soldOut = core.mobileEntryIsSoldOut(state, currentContext());
      state.mobileFocus.textContent = soldOut ? dialog.dataset.labelSoldOut : dialog.dataset.labelChooseSize;
      state.mobileFocus.disabled = soldOut;
    }
    state.controls.forEach(function (control) {
      var optionsRoot = control.querySelector('[data-mdv-stl-options]');
      var status = control.querySelector('[data-mdv-stl-status]');
      control.__mdvFocusKeeper.before();
      closeOptionPickers(control, 'rerender');
      optionsRoot.textContent = '';
      var panelSuppressesStatus = control.dataset.mdvStlSurface === 'panel' &&
        (state.pending || state.statusType === 'success');
      status.textContent = panelSuppressesStatus ? '' : state.status || (lockedReview ? dialog.dataset.labelLocked : '');
      status.dataset.state = panelSuppressesStatus ? '' : state.statusType || (lockedReview ? 'uncertain' : '');
      updateDisplayedPrice(control, state);

      if (!state.loaded) {
        var load = button(state.loading ? dialog.dataset.labelLoading : dialog.dataset.labelChooseSize, 'mdv-stl-control__load', 'load');
        load.disabled = Boolean(state.loading);
        optionsRoot.appendChild(load);
        control.__mdvFocusKeeper.after(Boolean(state.loading));
        return;
      }

      if (state.priceContextBlocked) {
        if (state.priceContextAfterWrite && control.dataset.mdvStlSurface === 'inline') {
          optionsRoot.appendChild(button(dialog.dataset.labelViewBag, 'mdv-stl-control__view-bag', 'view-bag'));
        }
        control.__mdvFocusKeeper.after(false);
        return;
      }

      var previousOptionSelected = true;
      state.options.forEach(function (option) {
        if (core.visibleOneSizeOption(option)) {
          var fixedField = document.createElement('div');
          fixedField.className = 'mdv-stl-control__field';
          var fixedLabel = document.createElement('p');
          fixedLabel.className = 'mdv-stl-control__option-label';
          fixedLabel.textContent = dialog.dataset.labelSize;
          var fixedValue = document.createElement('div');
          fixedValue.className = 'mdv-stl-control__fixed-option';
          fixedValue.textContent = dialog.dataset.labelOneSize;
          fixedValue.setAttribute('aria-label', dialog.dataset.labelSize + ': ' + dialog.dataset.labelOneSize);
          fixedField.appendChild(fixedLabel);
          fixedField.appendChild(fixedValue);
          optionsRoot.appendChild(fixedField);
          return;
        }
        if (core.autoSelectSingleOption(option)) return;
        var field = document.createElement('div');
        field.className = 'mdv-stl-control__field';
        var label = document.createElement('label');
        label.textContent = option.name;
        createOptionPicker(state, dialog, control, field, label, option, previousOptionSelected);
        optionsRoot.appendChild(field);
        if (!state.selected[String(option.position)]) previousOptionSelected = false;
      });

      if (state.preorder) {
        var preorder = document.createElement('p');
        preorder.className = 'mdv-stl-control__preorder';
        preorder.textContent = state.dispatch ? dialog.dataset.labelShipsBy.replace('__DATE__', core.formatDispatch(state.dispatch)) : dialog.dataset.labelPreorder;
        optionsRoot.appendChild(preorder);
      }

      var addLabel = dialog.dataset.labelAdd;
      var variantContext = state.context || currentContext();
      var locked = state.variant && writeLocks.has(variantContext, state.variant.id);
      var action = core.purchaseAction(state, currentContext(), locked);
      if (state.pending) addLabel = dialog.dataset.labelAdding;
      else if (!state.productAvailable || (state.variant && !state.variant.available)) addLabel = dialog.dataset.labelSoldOut;
      else if (state.preorderLabel && state.variant && state.variant.available) addLabel = state.preorderLabel;
      if (!state.pending && state.variant && state.collectionVariantId === String(state.variant.id)) addLabel = 'Add for store collection';
      var add = button(addLabel, 'mdv-stl-control__add', 'add');
      add.disabled = action === 'blocked';
      if (action === 'select') add.setAttribute('aria-haspopup', 'listbox');
      optionsRoot.appendChild(add);
      if (control.dataset.mdvStlSurface === 'inline' &&
          (lockedReview || state.statusType === 'success' || state.statusType === 'uncertain')) {
        optionsRoot.appendChild(button(dialog.dataset.labelViewBag, 'mdv-stl-control__view-bag', 'view-bag'));
      }
      control.__mdvFocusKeeper.after(Boolean(state.loading || state.resolving || state.pending));
    });
  }

  function applyProductData(state, data, preserveSelection) {
    state.country = data.country;
    state.currency = data.currency;
    state.priceContextBlocked = false;
    state.priceContextAfterWrite = false;
    state.productId = data.productId;
    state.productAvailable = data.productAvailable;
    state.preorder = data.preorder;
    state.preorderLabel = data.preorderLabel;
    state.dispatch = data.dispatch;
    state.options = data.options;
    state.loaded = true;
    state.selected = preserveSelection || state.selected || {};
    data.options.forEach(function (option) {
      if (core.autoSelectSingleOption(option)) state.selected[String(option.position)] = option.values[0].id;
    });
  }

  function blockPriceContext(state, dialog, afterWrite) {
    state.productId = '';
    state.productAvailable = false;
    state.country = '';
    state.currency = '';
    state.preorder = false;
    state.preorderLabel = '';
    state.dispatch = '';
    state.options = [];
    state.selected = {};
    state.variant = null;
    state.loaded = true;
    state.priceContextBlocked = true;
    state.priceContextAfterWrite = Boolean(afterWrite);
    setStatus(state, afterWrite ? dialog.dataset.labelPriceContextAfterAdd : dialog.dataset.labelPriceContext, 'error');
  }

  function responseContextMatches(data, dialog, requestCountry, requestCurrency) {
    return core.productContextStatus(data, requestCountry, requestCurrency) === 'matching' &&
      core.productContextStatus({ country: dialog.dataset.market, currency: dialog.dataset.currency }, requestCountry, requestCurrency) === 'matching';
  }

  function loadState(state, dialog) {
    if (dialog.__mdvDestroyed || state.instanceGeneration !== dialog.__mdvInstanceGeneration) return Promise.resolve();
    var liveContext = currentContext();
    var liveUrl = window.location.origin + core.routeRoot(window.Shopify) + 'products/' + encodeURIComponent(state.handle);
    if (core.invalidateProductContext(state, liveContext, liveUrl)) renderState(state, dialog);
    if (state.loading) return state.loading;
    if (!core.shouldLoadProduct(state)) return Promise.resolve();
    var loadContext = liveContext;
    var requestCountry = dialog.__mdvPageMarket || dialog.dataset.market;
    var requestCurrency = dialog.__mdvPageCurrency || dialog.dataset.currency;
    var loadGeneration = ++state.loadGeneration;
    state.context = loadContext;
    var loadPromise = fetchWithTimeout(sectionUrl(state, dialog), {}, productFetchTimeout)
      .then(function (response) {
        if (!response.ok) throw new Error('product-load-' + response.status);
        return response.text();
      })
      .then(function (html) {
        if (dialog.__mdvDestroyed || state.instanceGeneration !== dialog.__mdvInstanceGeneration ||
            !core.productLoadIsCurrent(state, loadGeneration, loadContext, currentContext())) return;
        var data = parseProductHtml(html);
        if (!responseContextMatches(data, dialog, requestCountry, requestCurrency)) {
          blockPriceContext(state, dialog, false);
          renderState(state, dialog);
          return;
        }
        applyProductData(state, data);
        setStatus(state, '', '');
        renderState(state, dialog);
        return resolveIfComplete(state, dialog);
      })
      .catch(function () {
        if (dialog.__mdvDestroyed || state.instanceGeneration !== dialog.__mdvInstanceGeneration ||
            !core.productLoadIsCurrent(state, loadGeneration, loadContext, currentContext())) return;
        state.loaded = false;
        setStatus(state, dialog.dataset.labelLoadError, 'error');
        renderState(state, dialog);
      })
      .finally(function () {
        if (dialog.__mdvDestroyed || state.instanceGeneration !== dialog.__mdvInstanceGeneration) return;
        if (state.loading !== loadPromise) return;
        state.loading = null;
        var currentLoadContext = currentContext();
        var currentLoadUrl = window.location.origin + core.routeRoot(window.Shopify) + 'products/' + encodeURIComponent(state.handle);
        core.invalidateProductContext(state, currentLoadContext, currentLoadUrl);
        renderState(state, dialog);
      });
    state.loading = loadPromise;
    renderState(state, dialog);
    return state.loading;
  }

  function resolveIfComplete(state, dialog) {
    if (dialog.__mdvDestroyed || state.instanceGeneration !== dialog.__mdvInstanceGeneration) return Promise.resolve();
    var requestIds = selectedIds(state);
    var completeSelection = allSelected(state);
    if (!requestIds.length || state.resolving || state.pending) return Promise.resolve();
    var requestNumber = ++state.requestNumber;
    var requestCountry = dialog.__mdvPageMarket || dialog.dataset.market;
    var requestCurrency = dialog.__mdvPageCurrency || dialog.dataset.currency;
    state.resolving = true;
    state.variant = null;
    renderState(state, dialog);
    return fetchWithTimeout(sectionUrl(state, dialog, requestIds), {}, productFetchTimeout)
      .then(function (response) {
        if (!response.ok) throw new Error('variant-load-' + response.status);
        return response.text();
      })
      .then(function (html) {
        if (dialog.__mdvDestroyed || state.instanceGeneration !== dialog.__mdvInstanceGeneration ||
            requestNumber !== state.requestNumber || state.context !== currentContext()) return;
        var data = parseProductHtml(html);
        if (!responseContextMatches(data, dialog, requestCountry, requestCurrency)) {
          blockPriceContext(state, dialog, false);
          return;
        }
        var preserved = Object.assign({}, state.selected);
        applyProductData(state, data, preserved);
        if (!completeSelection) {
          state.variant = null;
          setStatus(state, '', '');
          return;
        }
        if (!data.variantId || !core.selectionMatches(requestIds, data.selectedOptionIds)) {
          setStatus(state, dialog.dataset.labelUnavailable, 'error');
          state.variant = null;
          return;
        }
        state.variant = {
          id: data.variantId,
          available: data.available,
          tracked: data.tracked,
          policy: data.policy,
          requiresShipping: data.requiresShipping,
          price: data.price,
          formattedPrice: data.formattedPrice,
          compareAtPrice: data.compareAtPrice,
          formattedCompareAtPrice: data.formattedCompareAtPrice
        };
        if (!data.available) setStatus(state, dialog.dataset.labelSoldOut, 'error');
        else setStatus(state, '', '');
      })
      .catch(function () {
        if (dialog.__mdvDestroyed || state.instanceGeneration !== dialog.__mdvInstanceGeneration ||
            requestNumber !== state.requestNumber) return;
        state.variant = null;
        setStatus(state, dialog.dataset.labelLoadError, 'error');
      })
      .finally(function () {
        if (dialog.__mdvDestroyed || state.instanceGeneration !== dialog.__mdvInstanceGeneration) return;
        if (requestNumber === state.requestNumber) state.resolving = false;
        renderState(state, dialog);
      });
  }

  function cartUrl(path) {
    return core.routeRoot(window.Shopify) + path.replace(/^\//, '');
  }

  function getCart() {
    return fetchWithTimeout(cartUrl('cart.js'), { headers: { Accept: 'application/json' } }, 5000)
      .then(function (response) {
        if (!response.ok) throw new Error('cart-read-' + response.status);
        return response.json();
      });
  }

  function openBagDrawer(dialog, sourceControl) {
    if (!dialog || dialog.__mdvDestroyed || !dialog.isConnected) return false;
    var cart = document.querySelector('.min-cart');
    var trigger = document.querySelector('.header li.cart a');
    var bagTab = cart && cart.querySelector('.cart_tab .cart_tab_link1');
    var config = document.querySelector('[data-mdv-bag-config]');
    var feedback = sourceControl ? sourceControl.querySelector('[data-mdv-stl-status]') :
      dialog.querySelector('[data-mdv-stl-global-status]');
    var wasOpen = dialog.open || dialog.hasAttribute('open');
    function unavailable() {
      if (wasOpen && !dialog.open) {
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        document.body.classList.add('mdv-stl-dialog-open');
        var returnTarget = dialog.querySelector('[data-mdv-stl-view-bag]:not([hidden]), [data-mdv-stl-close]');
        if (returnTarget) returnTarget.focus({ preventScroll: true });
      }
      if (feedback && config) feedback.textContent = config.getAttribute('data-label-error');
      return false;
    }
    if (!cart || !trigger || !bagTab || document.body.classList.contains('template-cart') ||
        !window.MDVBagContext || typeof window.MDVBagContext.refresh !== 'function') return unavailable();
    if (wasOpen) closeDialog(dialog);
    else closeOptionPickers(dialog.closest('.product-page--root') || dialog, 'bag');
    // Refresh through the existing context guard before revealing the drawer.
    // This read never resolves or clears an uncertain add lock.
    // Keep the theme's normal focus, scroll lock and bag-tab behaviour. Prevent
    // default navigation even if a future header change removes its click handler.
    function preventNavigation(event) { event.preventDefault(); }
    trigger.addEventListener('click', preventNavigation);
    try {
      refreshBag();
      trigger.click();
      if (!bagTab.classList.contains('active')) bagTab.click();
    }
    catch (error) { return unavailable(); }
    finally { trigger.removeEventListener('click', preventNavigation); }
    if (!cart.classList.contains('active')) return unavailable();
    return true;
  }

  function refreshBag() {
    if (!(window.MDVBagContext && typeof window.MDVBagContext.refresh === 'function')) return null;
    return window.MDVBagContext.refresh().then(function (result) {
      if (!result || result.kind !== 'committed') return result;
      var cart = result.snapshot;
      document.dispatchEvent(new CustomEvent('mdv:cart-updated', { detail: { cart: cart, source: 'shop_the_look' } }));
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart, source: 'shop_the_look' } }));
      return result;
    });
  }

  function snapshotIsCurrent(state, snapshot) {
    return state.writeGeneration === snapshot.generation &&
      state.variant && String(state.variant.id) === String(snapshot.variant.id) &&
      currentContext() === snapshot.context;
  }

  function clearWriteLock(snapshot) {
    writeLocks.clear(snapshot.context, snapshot.variant.id);
  }

  function markConfirmed(state, dialog, snapshot, cart, adjustedMessage) {
    state.pending = false;
    if (!snapshotIsCurrent(state, snapshot)) return;
    clearWriteLock(snapshot);
    var confirmation = adjustedMessage ? dialog.dataset.labelAdded + ' ' + adjustedMessage : dialog.dataset.labelAdded;
    setStatus(state, confirmation, 'success');
    dialog.querySelector('[data-mdv-stl-view-bag]').hidden = false;
    dialog.querySelector('[data-mdv-stl-global-status]').textContent = confirmation;
    analytics(dialog, 'shop_the_look_add_confirmed', {
      entryPoint: snapshot.entryPoint,
      productId: snapshot.productId,
      variantId: snapshot.variant.id,
      value: snapshot.variant.price / 100
    }, { startAllowed: snapshot.analyticsAllowed });
    refreshBag(cart);
    renderState(state, dialog);
  }

  function markUnconfirmed(state, dialog, snapshot) {
    state.pending = false;
    if (!snapshotIsCurrent(state, snapshot)) return;
    setStatus(state, dialog.dataset.labelUnconfirmed, 'uncertain');
    dialog.querySelector('[data-mdv-stl-view-bag]').hidden = false;
    dialog.querySelector('[data-mdv-stl-global-status]').textContent = dialog.dataset.labelUnconfirmed;
    analytics(dialog, 'shop_the_look_add_unconfirmed', {
      entryPoint: snapshot.entryPoint,
      productId: snapshot.productId,
      variantId: snapshot.variant.id,
      value: snapshot.variant.price / 100
    }, { startAllowed: snapshot.analyticsAllowed });
    renderState(state, dialog);
  }

  function markPriceContextAfterWrite(state, dialog) {
    state.pending = false;
    blockPriceContext(state, dialog, true);
    dialog.querySelector('[data-mdv-stl-view-bag]').hidden = false;
    dialog.querySelector('[data-mdv-stl-global-status]').textContent = dialog.dataset.labelPriceContextAfterAdd;
    renderState(state, dialog);
  }

  function handleCartWriteResult(result, state, dialog, snapshot) {
    if (dialog.__mdvDestroyed || state.instanceGeneration !== dialog.__mdvInstanceGeneration) return;
    state.pending = false;
    state.writeDispatched = false;
    if (!snapshotIsCurrent(state, snapshot)) return;
    if (result.kind === 'confirmed') {
      markConfirmed(state, dialog, snapshot, result.cart || null, result.message || '');
      return;
    }
    if (result.kind === 'confirmed_context_mismatch' || result.kind === 'uncertain_context_mismatch') {
      markPriceContextAfterWrite(state, dialog);
      return;
    }
    if (result.kind === 'aborted_before_write') {
      blockPriceContext(state, dialog, false);
      renderState(state, dialog);
      return;
    }
    if (result.kind === 'rejected') {
      clearWriteLock(snapshot);
      setStatus(state, result.message || dialog.dataset.labelAddError, 'error');
      analytics(dialog, 'shop_the_look_add_rejected', {
        entryPoint: snapshot.entryPoint,
        productId: snapshot.productId,
        variantId: snapshot.variant.id,
        value: snapshot.variant.price / 100
      }, { startAllowed: snapshot.analyticsAllowed });
      renderState(state, dialog);
      return;
    }
    if (result.kind !== 'aborted_stale' && result.kind !== 'confirmed_stale') markUnconfirmed(state, dialog, snapshot);
  }

  function addItem(state, dialog) {
    if (dialog.__mdvDestroyed || state.instanceGeneration !== dialog.__mdvInstanceGeneration ||
        !state.variant || !state.variant.available || state.pending) return;
    var liveContext = currentContext();
    if (!core.purchaseContextIsCurrent(state.context, liveContext)) {
      state.requestNumber += 1;
      state.loaded = false;
      state.loading = null;
      state.resolving = false;
      state.variant = null;
      state.options = [];
      state.selected = {};
      state.context = liveContext;
      state.url = window.location.origin + core.routeRoot(window.Shopify) + 'products/' + encodeURIComponent(state.handle);
      setStatus(state, '', '');
      renderState(state, dialog);
      loadState(state, dialog);
      return;
    }
    if (core.productContextStatus(state, dialog.__mdvPageMarket || dialog.dataset.market, dialog.__mdvPageCurrency || dialog.dataset.currency) !== 'matching') {
      blockPriceContext(state, dialog, false);
      renderState(state, dialog);
      return;
    }
    var snapshot = {
      context: liveContext,
      expectedCurrency: dialog.__mdvPageCurrency || dialog.dataset.currency,
      entryPoint: state.entryPoint || 'existing_section',
      generation: ++state.writeGeneration,
      productId: state.productId,
      preorder: state.preorder,
      dispatch: state.dispatch,
      analyticsAllowed: telemetry.allowed(),
      variant: Object.freeze({
        id: state.variant.id,
        price: state.variant.price,
        available: state.variant.available,
        tracked: state.variant.tracked,
        policy: state.variant.policy,
        requiresShipping: state.variant.requiresShipping
      })
    };
    if (writeLocks.has(snapshot.context, snapshot.variant.id)) {
      setStatus(state, dialog.dataset.labelLocked, 'uncertain');
      dialog.querySelector('[data-mdv-stl-view-bag]').hidden = false;
      renderState(state, dialog);
      return;
    }

    state.pending = true;
    setStatus(state, dialog.dataset.labelAdding, '');
    renderState(state, dialog);
    var gate = window.MDVInventoryGate;
    if (!gate) {
      state.pending = false;
      setStatus(state, 'We could not verify this bag. Please retry.', 'error');
      renderState(state, dialog);
      return;
    }
    var collectionAction = state.collectionVariantId === String(snapshot.variant.id);
    var eligibilityInput = {
      productId: snapshot.productId,
      variantId: snapshot.variant.id,
      handle: state.handle,
      tracked: snapshot.variant.tracked,
      policy: snapshot.variant.policy,
      preorder: false,
      giftCard: false,
      requiresShipping: snapshot.variant.requiresShipping
    };
    gate.execute({
      action: collectionAction ? 'collection' : 'delivery',
      input: eligibilityInput,
      properties: core.preorderProperties(snapshot.preorder, snapshot.dispatch),
      isCurrent: function () {
        return !dialog.__mdvDestroyed && state.instanceGeneration === dialog.__mdvInstanceGeneration &&
          state.variant && String(state.variant.id) === String(snapshot.variant.id) &&
          snapshot.context === currentContext();
      }
    }, function (decision) {
      return core.runCartWriteMachine({
        context: snapshot.context,
        currentContext: function () {
          return dialog.__mdvDestroyed || state.instanceGeneration !== dialog.__mdvInstanceGeneration ? '' : currentContext();
        },
        variantId: snapshot.variant.id,
        quantity: 1,
        expectedCurrency: snapshot.expectedCurrency,
        rejectionMessage: dialog.dataset.labelAddError,
        readCart: getCart,
        wait: function () {
          return new Promise(function (resolve) { window.setTimeout(resolve, 700); });
        },
        beforeWrite: function () {
          state.writeDispatched = true;
          writeLocks.acquire(snapshot.context, snapshot.variant.id, {
            createdAt: Date.now(),
            quantity: 1,
            productId: snapshot.productId
          });
        },
        writeCart: function () {
          return fetchWithTimeout(cartUrl('cart/add.js'), {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{
                id: snapshot.variant.id,
                quantity: 1,
                properties: Object.assign({}, core.preorderProperties(snapshot.preorder, snapshot.dispatch), decision.properties || {})
              }]
            })
          }, cartWriteTimeout);
        }
      });
    }).then(function (gateResult) {
      if (gateResult.kind === 'stale') {
        state.pending = false;
        renderState(state, dialog);
        return;
      }
      if (gateResult.kind === 'collection_available') {
        state.pending = false;
        state.collectionVariantId = String(snapshot.variant.id);
        setStatus(state, gate.collectionMessage(gateResult.eligibleStores), '');
        renderState(state, dialog);
        return;
      }
      if (gateResult.kind === 'written' || gateResult.kind === 'written_unconfirmed') {
        state.collectionVariantId = '';
        handleCartWriteResult(gateResult.value, state, dialog, snapshot);
        return;
      }
      state.pending = false;
      var message = gateResult.kind === 'delivery_bag' ? gate.label('deliveryBag', dialog.dataset.labelAddError) :
        (gateResult.kind === 'collection_bag' ? gate.label('collectionBag', dialog.dataset.labelAddError) :
          (gateResult.kind === 'collection_location_conflict' ? gate.label('collectionLocationConflict', dialog.dataset.labelAddError) :
          (gateResult.kind === 'write_ambiguous' ? gate.label('ambiguous', dialog.dataset.labelAddError) :
            (gateResult.kind === 'reconcile_required' ? gate.label('reconcile', dialog.dataset.labelAddError) :
              gate.label('retry', dialog.dataset.labelAddError)))));
      setStatus(state, message, 'error');
      renderState(state, dialog);
    }).catch(function () {
      state.pending = false;
      setStatus(state, gate.label('ambiguous', dialog.dataset.labelAddError), 'error');
      renderState(state, dialog);
    });
  }

  function makePanelItem(state, card, dialog) {
    var item = document.createElement('article');
    item.className = 'mdv-stl-dialog__item';
    item.dataset.mdvStlPanelItem = state.handle;
    var media = document.createElement('div');
    media.className = 'mdv-stl-dialog__item-media';
    var imageLink = card.querySelector('.newshop-product-image a');
    if (imageLink) {
      var imageClone = imageLink.cloneNode(true);
      Array.prototype.forEach.call(imageClone.querySelectorAll('img'), function (image) {
        image.sizes = '130px';
        image.loading = 'lazy';
      });
      media.appendChild(imageClone);
    }
    var content = document.createElement('div');
    content.className = 'mdv-stl-dialog__item-content';
    var meta = card.querySelector('.mdv-tile-link');
    if (meta) content.appendChild(meta.cloneNode(true));
    content.appendChild(createControl(state, dialog, 'panel'));
    item.appendChild(media);
    item.appendChild(content);
    return item;
  }

  function createGalleryButton(dialog, target, extraClass, directChild) {
    if (!target) return null;
    var existing = directChild ? Array.prototype.find.call(target.children, function (child) {
      return child.dataset && child.dataset.mdvStlOpen === 'gallery';
    }) : target.querySelector('[data-mdv-stl-open="gallery"]');
    if (existing) return existing;
    var open = button(dialog.dataset.labelOpen, 'mdv-stl-gallery-button', 'open');
    if (extraClass) open.className += ' ' + extraClass;
    var icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('aria-hidden', 'true');
    icon.setAttribute('viewBox', '0 0 18 18');
    var bag = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    bag.setAttribute('d', 'M3.5 6.5h11v9h-11zM6.25 6.5V5.25a2.75 2.75 0 0 1 5.5 0V6.5');
    icon.appendChild(bag);
    open.insertBefore(icon, open.firstChild);
    open.dataset.mdvStlOpen = 'gallery';
    open.removeAttribute('data-mdv-stl-action');
    open.setAttribute('aria-controls', dialog.id);
    open.setAttribute('aria-haspopup', 'dialog');
    target.appendChild(open);
    return open;
  }

  function bindGalleryButton(dialog, buttonElement) {
    if (!buttonElement || buttonElement.dataset.mdvStlBound === 'true') return;
    buttonElement.dataset.mdvStlBound = 'true';
    buttonElement.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      openDialog(dialog, 'gallery', null, event);
    });
    observeExposure(dialog, buttonElement, 'gallery');
  }

  function ensureGalleryButtons(dialog, productRoot) {
    var desktopGallery = productRoot.querySelector('.product-page--images');
    var desktopSlides = Array.prototype.slice.call(productRoot.querySelectorAll('.product-page--images-container .prod_image_zoom'));
    desktopSlides.forEach(function (slide) {
      Array.prototype.forEach.call(slide.querySelectorAll('[data-mdv-stl-open="gallery"]'), function (buttonElement) {
        buttonElement.remove();
      });
    });
    if (desktopGallery && desktopSlides.some(function (slide) { return Boolean(slide.querySelector('a img')); })) {
      bindGalleryButton(dialog, createGalleryButton(dialog, desktopGallery, 'mdv-stl-gallery-button--desktop', true));
    }

    var mobileContainer = productRoot.querySelector('.product-page-mobile--images-container');
    if (!mobileContainer) return;
    Array.prototype.forEach.call(mobileContainer.querySelectorAll('.slick-cloned [data-mdv-stl-open="gallery"]'), function (buttonElement) {
      buttonElement.remove();
    });
    var slides = Array.prototype.slice.call(mobileContainer.querySelectorAll('.main-litght-img-mobile'));
    var indexes = core.originalStillIndexes(slides.map(function (slide) {
      return {
        cloned: slide.classList.contains('slick-cloned'),
        video: slide.classList.contains('main_external_video') || slide.classList.contains('main_html_mp4_video'),
        hasImage: Boolean(slide.querySelector('a img'))
      };
    }));
    indexes.forEach(function (index) {
      var slide = slides[index];
      var galleryButton = createGalleryButton(dialog, slide);
      galleryButton.tabIndex = slide.getAttribute('aria-hidden') === 'true' ? -1 : 0;
      bindGalleryButton(dialog, galleryButton);
    });
  }

  function openDialog(dialog, entryPoint, handle, triggerEvent) {
    if (dialog.__mdvDestroyed || dialog.dataset.mdvStlReady !== 'true') return;
    var keyboardOpen = !triggerEvent || triggerEvent.detail === 0;
    var opening = (dialog.__mdvOpening || 0) + 1;
    var openingContext = currentContext();
    dialog.__mdvOpening = opening;
    dialog.__mdvLastFocus = triggerEvent && triggerEvent.currentTarget || document.activeElement;
    dialog.__mdvStates.forEach(function (state) { state.entryPoint = entryPoint; });
    analytics(dialog, 'shop_the_look_opened', { entryPoint: entryPoint });
    document.body.classList.add('mdv-stl-dialog-open');
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    if (!keyboardOpen) {
      var title = dialog.querySelector('.mdv-stl-dialog__header h2');
      title.tabIndex = -1;
      title.focus({ preventScroll: true });
    }
    var openingFocus = document.activeElement;
    var allStates = Array.from(dialog.__mdvStates.values());
    allStates.forEach(function (state) {
      var liveUrl = window.location.origin + core.routeRoot(window.Shopify) + 'products/' + encodeURIComponent(state.handle);
      if (core.invalidateProductContext(state, openingContext, liveUrl)) renderState(state, dialog);
    });
    core.hydrateStates(allStates, function (state) { return loadState(state, dialog); });
    var target = handle ? dialog.querySelector('[data-mdv-stl-panel-item="' + CSS.escape(handle) + '"]') : dialog.querySelector('[data-mdv-stl-panel-item]');
    if (target) {
      var list = dialog.querySelector('[data-mdv-stl-list]');
      if (handle) {
        if (list) list.scrollTop = core.targetScrollTop(list.getBoundingClientRect(), target.getBoundingClientRect(), list.scrollTop);
      }
      var openingListScrollTop = list ? list.scrollTop : 0;
      var state = dialog.__mdvStates.get(target.dataset.mdvStlPanelItem);
      if (state) loadState(state, dialog).then(function () {
        if (dialog.__mdvDestroyed || state.instanceGeneration !== dialog.__mdvInstanceGeneration) return;
        if (!keyboardOpen) return;
        if (list && list.scrollTop !== openingListScrollTop) return;
        if (!handle && entryPoint === 'gallery' && openingListScrollTop !== 0) return;
        var dialogStillOpen = dialog.open || dialog.hasAttribute('open');
        if (!core.openingCanFocus(opening, dialog.__mdvOpening, openingContext, currentContext(), dialogStillOpen, target.isConnected, openingFocus, document.activeElement)) return;
        var first = target.querySelector('[data-mdv-stl-select]:not([disabled]), button:not([disabled])');
        if (!first) return;
        if (list) {
          var listRect = list.getBoundingClientRect();
          var firstRect = first.getBoundingClientRect();
          if (!Number.isFinite(listRect.top) || !Number.isFinite(listRect.bottom) ||
              !Number.isFinite(firstRect.top) || !Number.isFinite(firstRect.bottom) ||
              firstRect.top < listRect.top || firstRect.bottom > listRect.bottom) return;
        }
        first.focus({ preventScroll: true });
      });
    }
  }

  function closeDialog(dialog) {
    dialog.__mdvOpening = (dialog.__mdvOpening || 0) + 1;
    closeOptionPickers(dialog, 'dialog');
    if (typeof dialog.close === 'function' && dialog.open) dialog.close();
    else dialog.removeAttribute('open');
    document.body.classList.remove('mdv-stl-dialog-open');
    var opener = dialog.__mdvLastFocus;
    var card = opener && opener.closest('.swiper-slide');
    var productRoot = dialog.closest('.product-page--root') || document;
    var candidates = [opener, card && card.querySelector('a[href]')].concat(
      Array.prototype.slice.call(productRoot.querySelectorAll('[data-mdv-stl-open="gallery"]'))
    );
    var target = core.dialogReturnTarget(candidates);
    if (target) target.focus({ preventScroll: true });
  }

  function observeExposure(dialog, element, entryPoint) {
    var candidate = dialog.__mdvTelemetryCandidate;
    if (!element || !candidate || typeof IntersectionObserver !== 'function') return;
    if (dialog.dataset.mdvStlReady !== 'true' || !telemetryIdentityIsCurrent(dialog)) {
      dialog.__mdvPendingExposures = dialog.__mdvPendingExposures || [];
      if (!dialog.__mdvPendingExposures.some(function (pending) { return pending.element === element; })) {
        dialog.__mdvPendingExposures.push({ element: element, entryPoint: entryPoint });
      }
      return;
    }
    telemetry.registerExposure({
      id: element,
      candidate: candidate,
      currentKey: function () { return candidate.currentKey() + '|' + entryPoint; },
      current: function () {
        return element.isConnected && !dialog.__mdvDestroyed && telemetryIdentityIsCurrent(dialog);
      },
      currentRatio: function () { return core.visibleRatio(element, window.innerWidth, window.innerHeight); },
      detail: { entryPoint: entryPoint },
      observe: function (onChange) {
        var observer = new IntersectionObserver(function (entries) {
          if (entries.length) onChange();
        }, { threshold: [0.35] });
        observer.observe(element);
        return function () { observer.disconnect(); };
      }
    });
  }

  function flushPendingExposures(dialog) {
    if (!dialog || dialog.__mdvDestroyed || !telemetryIdentityIsCurrent(dialog)) return;
    var pending = dialog.__mdvPendingExposures || [];
    dialog.__mdvPendingExposures = [];
    pending.forEach(function (entry) {
      if (entry.element && entry.element.isConnected) observeExposure(dialog, entry.element, entry.entryPoint);
    });
  }

  function canSwap(context) {
    try {
      context = context || {};
      var dialog = context.dialog;
      if (!dialog) return !context.nextHasStl;
      if (dialog.__mdvDestroyed) return false;
      var open = dialog.open || dialog.hasAttribute('open');
      if (open) return false;
      if (dialog.dataset.mdvStlReady !== 'true') {
        return dialog.__mdvStates === undefined || dialog.__mdvStates === null;
      }
      if (!(dialog.__mdvStates instanceof Map) || !dialog.__mdvStates.size) return false;
      if (context.root && typeof context.root.contains === 'function' && !context.root.contains(dialog)) return false;
      var blocked = false;
      dialog.__mdvStates.forEach(function (state) {
        if (state && state.pending) blocked = true;
      });
      return !blocked;
    } catch (error) {
      return false;
    }
  }

  function destroy(dialog) {
    if (!dialog || dialog.__mdvDestroyed) return;
    function safely(callback) {
      try { callback(); } catch (error) {}
    }
    var productRoot = document;
    safely(function () { productRoot = dialog.closest('.product-page--root') || document; });
    safely(function () { dialog.__mdvDestroyed = true; });
    safely(function () { dialog.dataset.mdvStlDisabled = 'true'; });
    safely(function () { dialog.dataset.mdvStlReady = 'false'; });
    safely(function () { dialog.__mdvInstanceGeneration = Number(dialog.__mdvInstanceGeneration || 0) + 1; });
    safely(function () { dialog.__mdvOpening = Number(dialog.__mdvOpening || 0) + 1; });

    if (dialog.__mdvStates instanceof Map) {
      dialog.__mdvStates.forEach(function (state) {
        safely(function () { state.loadGeneration = Number(state.loadGeneration || 0) + 1; });
        safely(function () { state.requestNumber = Number(state.requestNumber || 0) + 1; });
        safely(function () { state.writeGeneration = Number(state.writeGeneration || 0) + 1; });
        safely(function () {
          if (state.hydrationObserver && typeof state.hydrationObserver.disconnect === 'function') {
            state.hydrationObserver.disconnect();
          }
        });
        safely(function () { state.hydrationObserver = null; });
      });
    }

    safely(function () { closeOptionPickers(productRoot, 'destroy'); });
    safely(function () {
      if (dialog.open || dialog.hasAttribute('open')) closeDialog(dialog);
    });
    safely(function () { document.body.classList.remove('mdv-stl-dialog-open'); });
    safely(function () {
      if (dialog.__mdvGalleryObserver && typeof dialog.__mdvGalleryObserver.disconnect === 'function') {
        dialog.__mdvGalleryObserver.disconnect();
      }
    });
    safely(function () { dialog.__mdvGalleryObserver = null; });
    safely(function () {
      if (dialog.__mdvTelemetryCandidate) telemetry.unregister(dialog.__mdvTelemetryCandidate);
    });
    safely(function () { dialog.__mdvTelemetryCandidate = null; });
    safely(function () { dialog.__mdvPendingExposures = []; });

    safely(function () {
      Array.prototype.forEach.call(productRoot.querySelectorAll('[data-mdv-stl-open="gallery"]'), function (element) {
        safely(function () { element.remove(); });
      });
    });
    safely(function () {
      Array.prototype.forEach.call(productRoot.querySelectorAll('.mdv-stl-inline'), function (element) {
        safely(function () { element.remove(); });
      });
    });
  }

  function init(dialog) {
    if (!dialog || dialog.__mdvDestroyed) return false;
    if (dialog.dataset.mdvStlReady === 'true') {
      if (!(dialog.__mdvStates instanceof Map) || !dialog.__mdvStates.size) throw new Error('invalid-ready-state');
      return true;
    }

    var productRoot = dialog.closest('.product-page--root') || document;
    var row = productRoot.querySelector('.new-shop-the-look');
    var cards = row ? Array.prototype.filter.call(row.querySelectorAll('.swiper-slide'), function (card) {
      return Boolean(card.querySelector('.newshop-product-image a[href*="/products/"]'));
    }) : [];
    if (!cards.length) {
      destroy(dialog);
      dialog.remove();
      return false;
    }

    try {
      dialog.__mdvPageMarket = dialog.dataset.market;
      dialog.__mdvPageCurrency = dialog.dataset.currency;
      dialog.__mdvInstanceGeneration = Number(dialog.__mdvInstanceGeneration || 0) + 1;
      dialog.__mdvPendingExposures = [];
      delete dialog.dataset.mdvStlDisabled;
      var states = new Map();
      dialog.__mdvStates = states;
      var list = dialog.querySelector('[data-mdv-stl-list]');
      if (!list) throw new Error('missing-dialog-list');
      list.textContent = '';
      Array.prototype.forEach.call(productRoot.querySelectorAll('.mdv-stl-inline'), function (element) { element.remove(); });

      cards.forEach(function (card) {
        var link = card.querySelector('.newshop-product-image a[href*="/products/"]');
        var handle = productHandle(link.href);
        if (!handle || states.has(handle)) return;
        var state = {
          handle: handle,
          url: link.href,
          context: currentContext(),
          instanceGeneration: dialog.__mdvInstanceGeneration,
          controls: [],
          options: [],
          selected: {},
          variant: null,
          productId: '',
          productAvailable: true,
          priceContextBlocked: false,
          priceContextAfterWrite: false,
          loaded: false,
          loading: null,
          resolving: false,
          pending: false,
          writeDispatched: false,
          loadGeneration: 0,
          writeGeneration: 0,
          requestNumber: 0,
          status: '',
          statusType: '',
          entryPoint: 'existing_section'
        };
        states.set(handle, state);
        var inline = document.createElement('div');
        inline.className = 'mdv-stl-inline';
        inline.appendChild(createControl(state, dialog, 'inline'));
        var mobileFocus = button(dialog.dataset.labelChooseSize, 'mdv-stl-mobile-focus', 'focus');
        state.mobileFocus = mobileFocus;
        mobileFocus.addEventListener('click', function (event) { openDialog(dialog, 'existing_section', handle, event); });
        inline.appendChild(mobileFocus);
        card.appendChild(inline);
        list.appendChild(makePanelItem(state, card, dialog));
        renderState(state, dialog);
        if (typeof IntersectionObserver === 'function') {
          state.hydrationObserver = new IntersectionObserver(function (entries) {
            if (dialog.__mdvDestroyed || state.instanceGeneration !== dialog.__mdvInstanceGeneration ||
                !entries.some(function (entry) { return entry.isIntersecting; })) return;
            state.hydrationObserver.disconnect();
            state.hydrationObserver = null;
            loadState(state, dialog);
          }, { threshold: [0.05] });
          state.hydrationObserver.observe(card);
        }
      });
      if (!states.size) throw new Error('missing-look-states');

      registerTelemetry(dialog);
      ensureGalleryButtons(dialog, productRoot);
      var mobileGallery = productRoot.querySelector('.product-page-mobile--images-container');
      if (mobileGallery && typeof MutationObserver === 'function') {
        dialog.__mdvGalleryObserver = new MutationObserver(function () {
          if (!dialog.__mdvDestroyed) ensureGalleryButtons(dialog, productRoot);
        });
        dialog.__mdvGalleryObserver.observe(mobileGallery, { childList: true });
      }
      dialog.querySelector('[data-mdv-stl-close]').addEventListener('click', function () { closeDialog(dialog); });
      dialog.addEventListener('cancel', function (event) { event.preventDefault(); closeDialog(dialog); });
      dialog.addEventListener('click', function (event) { if (event.target === dialog) closeDialog(dialog); });
      dialog.querySelector('[data-mdv-stl-view-bag]').addEventListener('click', function () {
        openBagDrawer(dialog);
      });
      observeExposure(dialog, row, 'existing_section');
      dialog.dataset.mdvStlReady = 'true';
      flushPendingExposures(dialog);
      telemetry.refresh();
      return true;
    } catch (error) {
      destroy(dialog);
      throw error;
    }
  }

  function initAll(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-mdv-stl-dialog]'), init);
  }

  function refreshAfterColourSwitch(event) {
    var scope = event && event.detail && event.detail.root;
    var dialogs = [];
    if (scope && typeof scope.matches === 'function' && scope.matches('[data-mdv-stl-dialog]')) dialogs.push(scope);
    if (scope && typeof scope.querySelectorAll === 'function') {
      dialogs = dialogs.concat(Array.prototype.slice.call(scope.querySelectorAll('[data-mdv-stl-dialog]')));
    }
    if (!dialogs.length) dialogs = Array.prototype.slice.call(document.querySelectorAll('[data-mdv-stl-dialog]'));
    dialogs.forEach(function (dialog) {
      if (dialog.dataset.mdvStlReady !== 'true' || dialog.__mdvDestroyed) return;
      flushPendingExposures(dialog);
    });
    telemetry.refresh();
  }

  function bindRuntimeListeners() {
    if (window.__mdvStlRuntimeListenersBound) return;
    window.__mdvStlRuntimeListenersBound = true;
    document.addEventListener('pointerdown', function () {
      document.documentElement.dataset.mdvStlInput = 'pointer';
    }, true);
    document.addEventListener('keydown', function () {
      document.documentElement.dataset.mdvStlInput = 'keyboard';
    }, true);
    document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
    document.addEventListener('mdv:colour-switched', refreshAfterColourSwitch);
    window.addEventListener('load', function () { attemptConsentFeature(); });
    document.addEventListener('visitorConsentCollected', function () {
      telemetry.refresh();
      attemptConsentFeature();
    });
    document.addEventListener('mdv:analytics-consent-state', function (event) {
      if (!event.detail || event.detail.state !== 'settled' || event.detail.analytics !== 'yes') return;
      telemetry.refresh();
      attemptConsentFeature();
    });
  }

  window.MdvShopTheLook = {
    contract: 1,
    version: '1.0.0',
    canSwap: canSwap,
    destroy: destroy,
    init: init,
    initAll: initAll
  };
  bindRuntimeListeners();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(document); }, { once: true });
  else initAll(document);
})();
